/**
 * Knowledge API
 * API calls for managing agent knowledge base documents
 * Uses userApiClient for /v1/agents endpoints
 */

import userApiClient from './userClient';
import apiClient from './client';

export interface KnowledgeDocument {
  doc_id: string;
  content: string;
  agent_type: string;
  metadata?: Record<string, any>;
  created_at?: string;
  relevance_score?: number;
}

export interface DocumentListResponse {
  documents: KnowledgeDocument[];
  total: number;
  agent_type: string;
  limit: number;
  offset: number;
}

export interface DocumentSearchResponse {
  documents: KnowledgeDocument[];
  query: string;
  total_found: number;
  agent_type: string;
}

export interface DocumentDeleteResponse {
  status: string;
  doc_id?: string;
  deleted_count?: number;
  message: string;
}

export interface DocumentQueryOptions {
  limit?: number;
  offset?: number;
  category?: string;
}

export interface CategoryInfo {
  name: string;
  doc_count: number;
}

export interface CategoryListResponse {
  categories: CategoryInfo[];
  agent_type: string;
}

export const knowledgeApi = {
  /**
   * List all documents in the knowledge base
   */
  list: async (
    agentType: string,
    options: DocumentQueryOptions = {}
  ): Promise<DocumentListResponse> => {
    const { limit = 50, offset = 0, category } = options;
    const params: Record<string, any> = { limit, offset };
    if (category) params.category = category;
    const { data } = await userApiClient.get(`/agents/${agentType}/knowledge`, {
      params,
    });
    return data;
  },

  /**
   * Get a specific document by ID
   */
  get: async (agentType: string, docId: string): Promise<KnowledgeDocument> => {
    const { data } = await userApiClient.get(`/agents/${agentType}/knowledge/${docId}`);
    return data;
  },

  /**
   * Create a new document
   */
  create: async (
    agentType: string,
    content: string,
    category: string,
    metadata?: Record<string, any>
  ): Promise<KnowledgeDocument> => {
    const { data } = await userApiClient.post(`/agents/${agentType}/knowledge`, {
      content,
      category,
      metadata,
    });
    return data;
  },

  /**
   * Update an existing document
   */
  update: async (
    agentType: string,
    docId: string,
    content: string,
    category?: string,
    metadata?: Record<string, any>
  ): Promise<KnowledgeDocument> => {
    const body: Record<string, any> = { content };
    if (category) body.category = category;
    if (metadata) body.metadata = metadata;
    const { data } = await userApiClient.put(`/agents/${agentType}/knowledge/${docId}`, body);
    return data;
  },

  /**
   * Delete a document
   */
  delete: async (agentType: string, docId: string): Promise<DocumentDeleteResponse> => {
    const { data } = await userApiClient.delete(`/agents/${agentType}/knowledge/${docId}`);
    return data;
  },

  /**
   * Clear all documents from knowledge base
   */
  clear: async (agentType: string): Promise<DocumentDeleteResponse> => {
    const { data } = await userApiClient.delete(`/agents/${agentType}/knowledge`);
    return data;
  },

  /**
   * Search documents in knowledge base
   */
  search: async (
    agentType: string,
    query: string,
    limit: number = 5,
    category?: string
  ): Promise<DocumentSearchResponse> => {
    const body: Record<string, any> = { query, limit };
    if (category) body.category = category;
    const { data } = await userApiClient.post(`/agents/${agentType}/knowledge/search`, body);
    return data;
  },

  /**
   * Upload a file to knowledge base
   */
  uploadFile: async (
    agentType: string,
    file: File,
    category: string,
    name?: string,
    description?: string,
    metadata?: Record<string, any>
  ): Promise<KnowledgeDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (name) formData.append('name', name);
    if (description) formData.append('description', description);
    if (metadata) formData.append('metadata', JSON.stringify(metadata));

    const { data } = await userApiClient.post(
      `/agents/${agentType}/knowledge/from-file`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  },

  /**
   * Import document from URL
   */
  importFromUrl: async (
    agentType: string,
    url: string,
    category: string,
    name?: string,
    description?: string,
    metadata?: Record<string, any>
  ): Promise<KnowledgeDocument> => {
    const { data } = await userApiClient.post(`/agents/${agentType}/knowledge/from-url`, {
      url,
      category,
      name,
      description,
      metadata,
    });
    return data;
  },

  /**
   * List categories with document counts
   */
  listCategories: async (agentType: string): Promise<CategoryListResponse> => {
    const { data } = await userApiClient.get(`/agents/${agentType}/knowledge/categories`);
    return data;
  },

  /**
   * Suggest a category for content based on agent config
   */
  suggestCategory: async (
    agentType: string,
    content: string,
  ): Promise<{ suggested_category: string; existing_categories: string[]; style_categories?: string[] }> => {
    const { data } = await userApiClient.post(
      `/agents/${agentType}/knowledge/suggest-category`,
      { content },
    );
    return data;
  },

  /**
   * Bulk create documents (one per content string)
   * category='auto' triggers LLM auto-categorization per doc
   */
  bulkCreate: async (
    agentType: string,
    contents: string[],
    category: string = 'auto',
  ): Promise<{ created: number; failed: number; total: number; categories?: Record<string, number> }> => {
    const { data } = await userApiClient.post(
      `/agents/${agentType}/knowledge/bulk`,
      { contents, category },
    );
    return data;
  },

  /**
   * Re-categorize all documents using LLM style classification
   */
  recategorize: async (
    agentType: string,
  ): Promise<{ total: number; updated: number; failed: number; categories: Record<string, number> }> => {
    const { data } = await userApiClient.post(
      `/agents/${agentType}/knowledge/recategorize`,
    );
    return data;
  },

  /**
   * Search the web for knowledge curation
   */
  searchWeb: async (query: string, count: number = 8): Promise<{
    query: string;
    results: { title: string; url: string; snippet: string; summary: string }[];
    total: number;
    error?: string;
  }> => {
    const { data } = await apiClient.post('/knowledge/search-web', { query, count });
    return data;
  },

  /**
   * Curate web content and import into knowledge base
   */
  curate: async (
    agentType: string,
    urls: string[],
    prompt: string,
    category: string,
    metadata?: Record<string, any>
  ): Promise<{
    documents: { doc_id: string; content: string; source_url: string; agent_type: string; category: string }[];
    errors: { url: string; error: string }[];
  }> => {
    const { data } = await userApiClient.post(`/agents/${agentType}/knowledge/curate`, {
      urls,
      prompt,
      category,
      metadata,
    });
    return data;
  },
};

export default knowledgeApi;
